import {
  Alert,
  Box,
  Button,
  Card,
  Container,
  Divider,
  FormControl,
  FormLabel,
  Input,
  Stack,
  Typography,
} from '@mui/joy';
import React, { useEffect, useState } from 'react';
import { DEFAULT_RATES, fetchRates, RATES_URL, Rates } from '../hooks/useRates';

interface OverrideForm {
  id: string;
  startDate: string;
  endDate: string;
  rooms: Record<string, string>;
  extraGuestFee: string;
}

const TOKEN_KEY = 'admin-token';

const ROOM_CATEGORIES: { key: string; label: string; note: string }[] = [
  { key: 'king-single', label: 'King / Single Bed', note: 'Up to 2 guests' },
  { key: 'full-double', label: 'Full / Double Bed', note: 'Up to 4 guests' },
];

const getToken = () => {
  try {
    return sessionStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
};

const AdminPage: React.FC = () => {
  const [token, setToken] = useState<string | null>(getToken);
  const [password, setPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  const [loggingIn, setLoggingIn] = useState(false);

  const [rooms, setRooms] = useState<Record<string, string>>({});
  const [extraGuestFee, setExtraGuestFee] = useState('');
  const [overrides, setOverrides] = useState<OverrideForm[]>([]);
  const [updatedAt, setUpdatedAt] = useState<string | null>(null);
  const [status, setStatus] = useState<{ type: 'success' | 'danger'; text: string } | null>(null);
  const [saving, setSaving] = useState(false);

  const applyRates = (r: Rates) => {
    setRooms(Object.fromEntries(Object.entries(r.rooms).map(([k, v]) => [k, String(v)])));
    setExtraGuestFee(String(r.extraGuestFee));
    setOverrides(
      r.overrides.map((o) => ({
        id: o.id,
        startDate: o.startDate,
        endDate: o.endDate,
        rooms: Object.fromEntries(Object.entries(o.rooms).map(([k, v]) => [k, String(v)])),
        extraGuestFee: String(o.extraGuestFee),
      })),
    );
    setUpdatedAt(r.updatedAt);
  };

  const logout = (message?: string) => {
    try {
      sessionStorage.removeItem(TOKEN_KEY);
    } catch {}
    setToken(null);
    if (message) setLoginError(message);
  };

  useEffect(() => {
    if (!token) return;
    fetchRates()
      .then(applyRates)
      .catch(() => {
        applyRates(DEFAULT_RATES);
        setStatus({ type: 'danger', text: 'Could not load current prices.' });
      });
  }, [token]);

  const login = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoggingIn(true);
    setLoginError('');
    try {
      const res = await fetch('/.netlify/functions/admin-login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Login failed');
      try {
        sessionStorage.setItem(TOKEN_KEY, data.token);
      } catch {}
      setToken(data.token);
      setPassword('');
    } catch (err) {
      setLoginError(err instanceof Error ? err.message : 'Login failed');
    } finally {
      setLoggingIn(false);
    }
  };

  const addOverride = () =>
    setOverrides([
      ...overrides,
      { id: crypto.randomUUID(), startDate: '', endDate: '', rooms: { ...rooms }, extraGuestFee },
    ]);

  const updateOverride = (id: string, patch: Partial<OverrideForm>) =>
    setOverrides(overrides.map((o) => (o.id === id ? { ...o, ...patch } : o)));

  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus(null);

    const toNums = (r: Record<string, string>) =>
      Object.fromEntries(Object.entries(r).map(([k, v]) => [k, v.trim() === '' ? NaN : Number(v)]));
    const payload = {
      rooms: toNums(rooms),
      extraGuestFee: extraGuestFee.trim() === '' ? NaN : Number(extraGuestFee),
      overrides: overrides.map((o) => ({
        id: o.id,
        startDate: o.startDate,
        endDate: o.endDate,
        rooms: toNums(o.rooms),
        extraGuestFee: o.extraGuestFee.trim() === '' ? NaN : Number(o.extraGuestFee),
      })),
    };
    const values = [
      ...Object.values(payload.rooms),
      payload.extraGuestFee,
      ...payload.overrides.flatMap((o) => [...Object.values(o.rooms), o.extraGuestFee]),
    ];
    if (values.some((v) => !Number.isFinite(v) || v < 0)) {
      setStatus({ type: 'danger', text: 'Enter a valid price (0 or more) in every field.' });
      return;
    }
    if (payload.overrides.some((o) => !o.startDate || !o.endDate || o.startDate > o.endDate)) {
      setStatus({ type: 'danger', text: 'Each special price needs a start date on or before its end date.' });
      return;
    }
    const sorted = [...payload.overrides].sort((a, b) => a.startDate.localeCompare(b.startDate));
    if (sorted.some((o, i) => i > 0 && o.startDate <= sorted[i - 1].endDate)) {
      setStatus({ type: 'danger', text: "Special price date ranges can't overlap." });
      return;
    }

    setSaving(true);
    try {
      const res = await fetch(RATES_URL, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(payload),
      });
      if (res.status === 401) return logout('Your session expired. Please sign in again.');
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Save failed');
      applyRates(data);
      setStatus({ type: 'success', text: 'Prices saved and now live on the website.' });
    } catch (err) {
      setStatus({ type: 'danger', text: err instanceof Error ? err.message : 'Save failed' });
    } finally {
      setSaving(false);
    }
  };

  if (!token) {
    return (
      <Container maxWidth="xs" sx={{ py: 10 }}>
        <Card component="form" onSubmit={login} sx={{ p: 4 }}>
          <Typography level="h3" sx={{ mb: 0.5 }}>Admin sign in</Typography>
          <Typography level="body-sm" sx={{ mb: 2 }}>Executive Inn Baker</Typography>
          {loginError && <Alert color="danger" sx={{ mb: 2 }}>{loginError}</Alert>}
          <FormControl required>
            <FormLabel>Password</FormLabel>
            <Input
              type="password"
              autoFocus
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </FormControl>
          <Button type="submit" loading={loggingIn} sx={{ mt: 3 }}>Sign in</Button>
        </Card>
      </Container>
    );
  }

  return (
    <Container maxWidth="sm" sx={{ py: 8 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
        <Typography level="h3">Room pricing</Typography>
        <Button variant="plain" color="neutral" onClick={() => logout()}>Sign out</Button>
      </Stack>

      <Card component="form" onSubmit={save} sx={{ p: 4 }}>
        {status && <Alert color={status.type === 'success' ? 'success' : 'danger'} sx={{ mb: 2 }}>{status.text}</Alert>}

        <Stack spacing={3}>
          {ROOM_CATEGORIES.map(({ key, label, note }) => (
            <FormControl key={key}>
              <FormLabel>{label} — regular per night</FormLabel>
              <Input
                type="number"
                startDecorator="$"
                slotProps={{ input: { min: 0, step: '0.01' } }}
                value={rooms[key] ?? ''}
                onChange={(e) => setRooms({ ...rooms, [key]: e.target.value })}
              />
              <Typography level="body-xs" sx={{ mt: 0.5 }}>{note}</Typography>
            </FormControl>
          ))}

          <FormControl>
            <FormLabel>Extra guest fee (3rd and 4th guest) — per night</FormLabel>
            <Input
              type="number"
              startDecorator="$"
              slotProps={{ input: { min: 0, step: '0.01' } }}
              value={extraGuestFee}
              onChange={(e) => setExtraGuestFee(e.target.value)}
            />
          </FormControl>
        </Stack>

        <Divider sx={{ my: 4 }} />
        <Typography level="title-lg">Special pricing</Typography>
        <Typography level="body-sm" sx={{ mb: 2 }}>
          These prices apply only to nights between the start and end dates (inclusive). All other
          nights use the regular prices above, even within the same booking.
        </Typography>

        <Stack spacing={2}>
          {overrides.map((o) => (
            <Card key={o.id} variant="outlined" sx={{ p: 2 }}>
              <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
                <FormControl sx={{ flex: 1 }}>
                  <FormLabel>Start date</FormLabel>
                  <Input
                    type="date"
                    value={o.startDate}
                    onChange={(e) => updateOverride(o.id, { startDate: e.target.value })}
                  />
                </FormControl>
                <FormControl sx={{ flex: 1 }}>
                  <FormLabel>End date</FormLabel>
                  <Input
                    type="date"
                    value={o.endDate}
                    onChange={(e) => updateOverride(o.id, { endDate: e.target.value })}
                  />
                </FormControl>
              </Stack>
              <Stack spacing={2}>
                {ROOM_CATEGORIES.map(({ key, label }) => (
                  <FormControl key={key}>
                    <FormLabel>{label} — per night</FormLabel>
                    <Input
                      type="number"
                      startDecorator="$"
                      slotProps={{ input: { min: 0, step: '0.01' } }}
                      value={o.rooms[key] ?? ''}
                      onChange={(e) => updateOverride(o.id, { rooms: { ...o.rooms, [key]: e.target.value } })}
                    />
                  </FormControl>
                ))}
                <FormControl>
                  <FormLabel>Extra guest fee — per night</FormLabel>
                  <Input
                    type="number"
                    startDecorator="$"
                    slotProps={{ input: { min: 0, step: '0.01' } }}
                    value={o.extraGuestFee}
                    onChange={(e) => updateOverride(o.id, { extraGuestFee: e.target.value })}
                  />
                </FormControl>
              </Stack>
              <Button
                variant="plain"
                color="danger"
                sx={{ mt: 1, alignSelf: 'flex-start' }}
                onClick={() => setOverrides(overrides.filter((x) => x.id !== o.id))}
              >
                Remove
              </Button>
            </Card>
          ))}
          <Button variant="outlined" onClick={addOverride} sx={{ alignSelf: 'flex-start' }}>
            + Add special pricing
          </Button>
        </Stack>

        <Box sx={{ mt: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
          <Button type="submit" loading={saving}>Save &amp; publish</Button>
          {updatedAt && (
            <Typography level="body-xs">Last updated {new Date(updatedAt).toLocaleString()}</Typography>
          )}
        </Box>
      </Card>
    </Container>
  );
};

export default AdminPage;
